// TelcoTrack - Construction diary work summary display helpers
// Human-readable headlines for "Provedené práce" (addresses / coords, not raw object IDs).

import type { DiaryWorkSummaryEntry } from '../models/construction-diary.model';
import type { Object } from '../models/object.model';
import {
  buildWorkSummaryBaseLabel,
  buildChainedRoutePolylineFromSegments,
  diaryWorkSummarySegmentPath,
  filterDiaryNoteParts,
  formatWorkSummaryLocationHeadline,
  isDiaryExcavationObjectType,
  isVsoHp,
} from '@core/shared-bridge';
import { formatObjectAddress, getObjectTypeName } from '../../shared/utils/object.utils';

export { excavationHeadlineTemplate } from '@core/shared-bridge';
export interface BuildingAnchor {
  lat: number;
  lng: number;
  label: string;
}

export interface RoutePoint {
  lat: number;
  lng: number;
}

/** Typy objektů s adresou / parcelou — jeden batch dotaz na polygon při otevření deníku. */
export const DIARY_ADDRESS_ANCHOR_OBJECT_TYPES = ['BUDOVA'] as const;

/** Minimální vzdušná vzdálenost koncových bodů pro formát „od … po …“ (kratší trasa → „u …“). */
export const ROUTE_HEADLINE_SPAN_METERS = 50;

function toFiniteCoord(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export function isExcavationType(type: string | null | undefined): boolean {
  return isDiaryExcavationObjectType(type);
}

type ObjectWithSpatial = Object & {
  centroidLat?: number | string | null;
  centroidLng?: number | string | null;
  path?: [number, number][];
};

/** WGS84 bod — GeoJSON `location`, centroid z in-polygon API, nebo první bod `path`. */
export function extractObjectLatLng(obj: Object): { lat: number; lng: number } | null {
  const spatial = obj as ObjectWithSpatial;
  const cLat = toFiniteCoord(spatial.centroidLat);
  const cLng = toFiniteCoord(spatial.centroidLng);
  if (cLat != null && cLng != null) return { lat: cLat, lng: cLng };

  const loc = obj.location;
  if (loc?.coordinates && Array.isArray(loc.coordinates)) {
    const type = (loc.type || '').toLowerCase();
    if (type === 'point' && loc.coordinates.length >= 2) {
      const lng = toFiniteCoord(loc.coordinates[0]);
      const lat = toFiniteCoord(loc.coordinates[1]);
      if (lat != null && lng != null) return { lat, lng };
    }
    const ring = loc.coordinates[0];
    if (Array.isArray(ring) && ring.length > 0 && Array.isArray(ring[0]) && ring[0].length >= 2) {
      const lng = toFiniteCoord(ring[0][0]);
      const lat = toFiniteCoord(ring[0][1]);
      if (lat != null && lng != null) return { lat, lng };
    }
  }

  const path = spatial.path;
  if (Array.isArray(path) && path.length > 0 && Array.isArray(path[0]) && path[0].length >= 2) {
    const lng = toFiniteCoord(path[0][0]);
    const lat = toFiniteCoord(path[0][1]);
    if (lat != null && lng != null) return { lat, lng };
  }
  return null;
}

export function buildingAnchorFromObject(obj: Object): BuildingAnchor | null {
  const pos = extractObjectLatLng(obj);
  if (!pos) return null;
  const acqCp = obj.acquisition?.cisloPopisne?.trim() ?? '';
  const acqUlice = obj.acquisition?.ulice?.trim() ?? '';
  const addrFromAcquisition =
    acqCp || acqUlice
      ? formatObjectAddress({ cisloPopisne: acqCp, ulice: acqUlice || undefined })
      : '';
  const addr = formatObjectAddress(obj.addressPoint) || addrFromAcquisition;
  const obec = obj.addressPoint?.obec?.trim() || obj.acquisition?.mesto?.trim() || '';
  let label = '';
  if (addr && obec) label = `${addr}, ${obec}`;
  else if (addr) label = addr;
  else if (obj.parcelNumber?.trim()) label = `parc. ${obj.parcelNumber.trim()}`;
  else if (obec) label = obec;
  if (!label) return null;
  return { lat: pos.lat, lng: pos.lng, label };
}

export function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const r = 6_371_000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(a)));
}

function isSameAnchor(a: BuildingAnchor, b: BuildingAnchor): boolean {
  return a.lat === b.lat && a.lng === b.lng && a.label === b.label;
}

export function findNearestAnchor(
  lat: number,
  lng: number,
  buildings: BuildingAnchor[],
  exclude?: BuildingAnchor | null,
): BuildingAnchor | null {
  if (!buildings.length || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  let best: BuildingAnchor | null = null;
  let bestDist = Infinity;
  for (const b of buildings) {
    if (exclude && isSameAnchor(b, exclude)) continue;
    const d = haversineMeters(lat, lng, b.lat, b.lng);
    if (d < bestDist) {
      bestDist = d;
      best = b;
    }
  }
  return best;
}

export function collectGroupPoints(items: DiaryWorkSummaryEntry[]): RoutePoint[] {
  const pts: RoutePoint[] = [];
  for (const it of items) {
    const lat = toFiniteCoord(it.lat);
    const lng = toFiniteCoord(it.lng);
    if (lat != null && lng != null) pts.push({ lat, lng });
    const endLat = toFiniteCoord(it.endLat);
    const endLng = toFiniteCoord(it.endLng);
    if (endLat != null && endLng != null) pts.push({ lat: endLat, lng: endLng });
  }
  return pts;
}

/**
 * Celá trasa skupiny — řetězí segmenty přes sdílené konce (parita s mapou).
 */
export function buildWorkGroupRoutePolyline(items: DiaryWorkSummaryEntry[]): RoutePoint[] {
  if (!items.length) return [];

  const segments = items
    .map((it) => {
      const path = diaryWorkSummarySegmentPath(it);
      const objectId = it.objectId?.trim();
      if (!objectId || path.length < 2) return null;
      return { objectId, path };
    })
    .filter((seg): seg is { objectId: string; path: [number, number][] } => seg != null);

  if (!segments.length) {
    const pointFallback: RoutePoint[] = [];
    for (const it of items) {
      const lat = toFiniteCoord(it.lat);
      const lng = toFiniteCoord(it.lng);
      if (lat != null && lng != null) pointFallback.push({ lat, lng });
    }
    return pointFallback;
  }

  const anchorId = [...segments].map((s) => s.objectId).sort()[0];
  const chained = buildChainedRoutePolylineFromSegments(segments, anchorId);
  return chained.map(([lng, lat]) => ({ lat, lng }));
}

export interface RoutePreviewDimensions {
  widthPx: number;
  heightPx: number;
}

/** Rozměry mini mapy podle rozsahu trasy (min/max v pixelech). */
export function computeRoutePreviewDimensions(points: RoutePoint[]): RoutePreviewDimensions {
  const MIN_W = 140;
  const MAX_W = 260;
  const MIN_H = 100;
  const MAX_H = 200;

  if (!points.length) return { widthPx: MIN_W, heightPx: MIN_H };
  if (points.length === 1) return { widthPx: MIN_W, heightPx: MIN_H };

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }

  const midLat = (minLat + maxLat) / 2;
  const spanLatM = Math.max((maxLat - minLat) * 111_000, 5);
  const spanLngM = Math.max((maxLng - minLng) * 111_000 * Math.cos((midLat * Math.PI) / 180), 5);
  const diagonalM = Math.sqrt(spanLatM ** 2 + spanLngM ** 2);
  const extentT = Math.min(1, Math.max(0, (diagonalM - 15) / 400));
  const aspect = spanLngM / spanLatM;
  const normAspect = Math.min(2.5, Math.max(0.4, aspect));

  let widthPx = Math.round(MIN_W + extentT * (MAX_W - MIN_W));
  let heightPx = Math.round(MIN_H + extentT * (MAX_H - MIN_H));

  if (normAspect > 1.2) {
    widthPx = Math.min(MAX_W, Math.round(widthPx * Math.min(normAspect, 1.6)));
    heightPx = Math.max(MIN_H, Math.round(heightPx / Math.sqrt(normAspect)));
  } else if (normAspect < 0.8) {
    heightPx = Math.min(MAX_H, Math.round(heightPx / normAspect));
    widthPx = Math.max(MIN_W, Math.round(widthPx * normAspect));
  }

  return {
    widthPx: Math.min(MAX_W, Math.max(MIN_W, widthPx)),
    heightPx: Math.min(MAX_H, Math.max(MIN_H, heightPx)),
  };
}

/** Dva koncové body s největší vzdušnou vzdáleností — pro „od … po …“. */
export function extremeRouteEndpoints(
  points: RoutePoint[],
): { from: RoutePoint; to: RoutePoint } | null {
  if (!points.length) return null;
  if (points.length === 1) return { from: points[0], to: points[0] };
  let maxDist = -1;
  let from = points[0];
  let to = points[0];
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const d = haversineMeters(points[i].lat, points[i].lng, points[j].lat, points[j].lng);
      if (d > maxDist) {
        maxDist = d;
        from = points[i];
        to = points[j];
      }
    }
  }
  return { from, to };
}

export function formatCoordLabel(lat: number, lng: number): string {
  const latStr = `${Math.abs(lat).toFixed(5)}°${lat >= 0 ? 'N' : 'S'}`;
  const lngStr = `${Math.abs(lng).toFixed(5)}°${lng >= 0 ? 'E' : 'W'}`;
  return `${latStr}, ${lngStr}`;
}

/** Adresa nejbližšího kotvy (BUDOVA / parcela), jinak souřadnice ve čitelném tvaru. */
export function formatLocationLabel(
  lat: number,
  lng: number,
  buildings: BuildingAnchor[],
  options?: { exclude?: BuildingAnchor | null },
): string {
  const nearest = findNearestAnchor(lat, lng, buildings, options?.exclude);
  if (nearest?.label) return nearest.label;
  return formatCoordLabel(lat, lng);
}

export interface WorkGroupHeadlineResult {
  headline: string;
  fromLabel: string | null;
  toLabel: string | null;
}

export function dedupeBuildingAnchors(anchors: BuildingAnchor[]): BuildingAnchor[] {
  const seen = new Set<string>();
  const out: BuildingAnchor[] = [];
  for (const a of anchors) {
    const key = `${a.lat}|${a.lng}|${a.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

export function formatWorkGroupHeadline(
  group: {
    objectType: string;
    phaseTo?: string | null;
    items: DiaryWorkSummaryEntry[];
  },
  buildings: BuildingAnchor[],
  phaseLabel: string,
): WorkGroupHeadlineResult {
  const points = collectGroupPoints(group.items);
  const ends = extremeRouteEndpoints(points);
  const phaseTo = group.phaseTo?.trim() ?? '';
  const baseLabel = buildWorkSummaryBaseLabel(phaseLabel, group.objectType, phaseTo);

  if (!ends) {
    const count = group.items.length;
    if (!isExcavationType(group.objectType) && count > 1) {
      const typeName = getObjectTypeName(group.objectType) || group.objectType;
      return {
        headline: `${typeName} × ${count}: ${baseLabel}`,
        fromLabel: null,
        toLabel: null,
      };
    }
    return { headline: baseLabel, fromLabel: null, toLabel: null };
  }

  const routeSpanM = haversineMeters(ends.from.lat, ends.from.lng, ends.to.lat, ends.to.lng);
  const fromAnchor = findNearestAnchor(ends.from.lat, ends.from.lng, buildings);
  const fromLabel = fromAnchor?.label ?? formatCoordLabel(ends.from.lat, ends.from.lng);
  let toLabel = formatLocationLabel(ends.to.lat, ends.to.lng, buildings, {
    exclude: routeSpanM > ROUTE_HEADLINE_SPAN_METERS ? fromAnchor : null,
  });
  if (routeSpanM > ROUTE_HEADLINE_SPAN_METERS && toLabel === fromLabel) {
    toLabel = formatCoordLabel(ends.to.lat, ends.to.lng);
  }
  const headline = formatWorkSummaryLocationHeadline(
    baseLabel,
    group.objectType,
    fromLabel,
    toLabel,
    routeSpanM,
    ROUTE_HEADLINE_SPAN_METERS,
  );
  return { headline, fromLabel, toLabel };
}

export interface WorkGroupDisplayLine {
  label: string;
  items: string[];
}

const NOTE_PREFIXES = [
  'poznámka u objektu:',
  'poznámky u objektu',
  'popis objektu:',
  'koncept poznámky k fázi:',
  'poznámka k fázi:',
] as const;

const EXTRA_PREFIX_LABELS: ReadonlyArray<readonly [string, string]> = [
  ['počet svarů:', 'Počet svarů'],
  ['zafouknuté sekundární ok:', 'Zafouknuté OK'],
  ['délka záfuku:', 'Délka záfuku'],
  ['rozvaděč:', 'Rozvaděč'],
  ['zafouknuto z:', 'Rozvaděč'],
  ['technická zpráva vr:', 'Tech. zpráva VR'],
  ['spolupráce:', 'Spolupráce'],
];

function splitRichDescription(raw: string): string[] {
  return raw
    .split(/\s+·\s+/)
    .flatMap((chunk) => chunk.split(/\n+/))
    .map((p) => p.trim())
    .filter(Boolean);
}

function findPrefixed(parts: string[], prefix: string): string | null {
  const lower = prefix.toLowerCase();
  const match = parts.find((p) => p.toLowerCase().startsWith(lower));
  if (!match) return null;
  return match.slice(prefix.length).trim();
}

function splitSemicolonList(raw: string): string[] {
  return raw
    .split(/;\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function splitMiddotList(raw: string): string[] {
  return raw
    .split(/\s+·\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Povrchy / poznámky — `;` má přednost, jinak ` · ` (parity s activity timeline). */
function splitSemicolonOrMiddotList(raw: string): string[] {
  const bySemi = splitSemicolonList(raw);
  if (bySemi.length > 1) return bySemi;
  const byDot = splitMiddotList(raw);
  return byDot.length > 1 ? byDot : bySemi;
}

/** Rozdělí `specifikace:` na řádky jako activity timeline (`formatSpecItems`). */
function formatSpecDisplayItems(specRaw: string): string[] {
  const parts = specRaw
    .split(/,\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
  const items: string[] = [];
  let hpTotal: number | null = null;
  let hpNebyt: number | null = null;

  for (const part of parts) {
    const hpMatch = /^HP celkem\s+(\d+)/i.exec(part);
    if (hpMatch) {
      hpTotal = Number(hpMatch[1]);
      continue;
    }
    const nebytMatch = /^z toho nebyt\.?\s*HP\s+(\d+)/i.exec(part);
    if (nebytMatch) {
      hpNebyt = Number(nebytMatch[1]);
      continue;
    }
    const floorsMatch = /^(\d+)\s+podla/i.exec(part);
    if (floorsMatch) {
      items.push(`Počet pater: ${floorsMatch[1]}`);
      continue;
    }
    const flatsMatch = /^(\d+)\s+byt/i.exec(part);
    if (flatsMatch) {
      items.push(`Počet jednotek: ${flatsMatch[1]}`);
      continue;
    }
    if (part.trim()) items.push(part);
  }

  if (hpTotal != null && Number.isFinite(hpTotal)) {
    if (isVsoHp(hpTotal)) {
      items.unshift(`Počet HP: ${hpTotal}`);
    } else if (hpNebyt != null && Number.isFinite(hpNebyt)) {
      const byt = Math.max(0, hpTotal - hpNebyt);
      items.unshift(`Počet HP: ${byt}/${hpNebyt}`);
    } else {
      items.unshift(`Počet HP: ${hpTotal}`);
    }
  }

  return items;
}

function isPrefixedSectionPart(part: string): boolean {
  const lower = part.toLowerCase();
  const prefixes = [
    'materiál:',
    'specifikace:',
    'výkop / povrchy:',
    'bourání / rozvoz:',
    ...NOTE_PREFIXES,
    ...EXTRA_PREFIX_LABELS.map(([p]) => p),
  ];
  return prefixes.some((p) => lower.startsWith(p));
}

function dedupTrimmedStrings(arr: string[]): string[] {
  const set = new Set<string>();
  for (const v of arr) {
    const t = v.trim();
    if (t) set.add(t);
  }
  return [...set];
}

function pushDisplayLine(
  lines: WorkGroupDisplayLine[],
  label: string,
  items: string[],
): void {
  const clean = dedupTrimmedStrings(items);
  if (!clean.length) return;
  const existing = lines.find((l) => l.label === label);
  if (existing) {
    existing.items = dedupTrimmedStrings([...existing.items, ...clean]);
    return;
  }
  lines.push({ label, items: clean });
}

function isConsumedNotePart(part: string): boolean {
  const lower = part.toLowerCase();
  if (lower.startsWith('výkop / povrchy')) return true;
  if (lower === 'výkop / povrchy (uloženo)') return true;
  const prefixes = [
    'materiál:',
    'specifikace:',
    'bourání / rozvoz:',
    'vazba na rozvaděč',
    ...NOTE_PREFIXES,
    ...EXTRA_PREFIX_LABELS.map(([p]) => p),
  ];
  return prefixes.some((p) => lower.startsWith(p));
}

function collectNoteParts(notes: string[]): string[] {
  const parts: string[] = [];
  for (const note of notes) {
    if (typeof note !== 'string' || !note.trim()) continue;
    parts.push(...splitRichDescription(note));
  }
  return filterDiaryNoteParts(dedupTrimmedStrings(parts));
}

function surfaceFieldDuplicatesNote(
  formattedSurface: string,
  noteSurfaceItems: string[],
): boolean {
  const norm = formattedSurface.trim().toLowerCase();
  if (!norm) return true;
  return noteSurfaceItems.some((item) => item.toLowerCase().includes(norm));
}

/**
 * Rozdělí `surface` + `note` (change_description) do kategorií jako activity timeline.
 * Materiál ≠ povrch; duplicitní `surface` pole se vynechá, pokud je v poznámce `výkop / povrchy:`.
 */
export function buildWorkGroupDisplayLines(
  surfaces: string[],
  notes: string[],
  objectType: string,
  formatSurface: (raw: string) => string,
): WorkGroupDisplayLine[] {
  const parts = collectNoteParts(notes);
  const lines: WorkGroupDisplayLine[] = [];

  const material = findPrefixed(parts, 'materiál:');
  if (material) pushDisplayLine(lines, 'Materiál', splitSemicolonList(material));

  const spec = findPrefixed(parts, 'specifikace:');
  if (spec) pushDisplayLine(lines, 'Specifikace', formatSpecDisplayItems(spec));

  for (const [prefix, label] of EXTRA_PREFIX_LABELS) {
    const value = findPrefixed(parts, prefix);
    if (value) pushDisplayLine(lines, label, splitSemicolonOrMiddotList(value));
  }

  for (const part of parts) {
    if (part.toLowerCase().startsWith('vazba na rozvaděč')) {
      pushDisplayLine(lines, 'Rozvaděč', [part]);
    }
  }

  const surfaceItems: string[] = [];
  const surfaceContinuationParts = new Set<string>();
  let afterPovrchySection = false;
  for (const part of parts) {
    const lower = part.toLowerCase();
    if (lower.startsWith('výkop / povrchy:')) {
      const povrchyValue = part.slice('výkop / povrchy:'.length).trim();
      if (povrchyValue && !/^\s*\(uloženo\)\s*$/i.test(povrchyValue)) {
        surfaceItems.push(...splitSemicolonOrMiddotList(povrchyValue));
      }
      afterPovrchySection = true;
      continue;
    }
    if (afterPovrchySection && !isConsumedNotePart(part) && !isPrefixedSectionPart(part)) {
      surfaceContinuationParts.add(part);
      surfaceItems.push(part);
      continue;
    }
    afterPovrchySection = false;

    if (/^prohloubení\s/i.test(part)) {
      const item = part.replace(/^prohloubení\s/i, 'Prohloubení: ');
      if (!surfaceItems.some((i) => i.toLowerCase().includes('prohloubení'))) {
        surfaceItems.push(item);
      }
    }
    const bourani = findPrefixed([part], 'bourání / rozvoz:');
    if (bourani) surfaceItems.push(...splitSemicolonList(bourani));
  }

  const hasRichSurfacesFromNote = surfaceItems.length > 0;
  if (!hasRichSurfacesFromNote) {
    for (const raw of surfaces) {
      const formatted = formatSurface(raw);
      if (formatted) surfaceItems.push(formatted);
    }
  } else {
    for (const raw of surfaces) {
      const formatted = formatSurface(raw);
      if (formatted && !surfaceFieldDuplicatesNote(formatted, surfaceItems)) {
        surfaceItems.push(formatted);
      }
    }
  }

  if (surfaceItems.length > 0) {
    pushDisplayLine(lines, 'Povrchy', surfaceItems);
  }

  const noteItems: string[] = [];
  for (const prefix of NOTE_PREFIXES) {
    const value = findPrefixed(parts, prefix);
    if (value) noteItems.push(...splitMiddotList(value));
  }
  for (const part of parts) {
    if (isConsumedNotePart(part)) continue;
    if (surfaceContinuationParts.has(part)) continue;
    if (/^prohloubení\s/i.test(part)) continue;
    noteItems.push(part);
  }

  if (noteItems.length > 0) {
    pushDisplayLine(lines, 'Poznámky', noteItems);
  } else if (
    !lines.length &&
    parts.length > 0 &&
    !isExcavationType(objectType)
  ) {
    pushDisplayLine(lines, 'Poznámky', parts);
  }

  return lines;
}

/** Klíč seskupení: jeden batch = jedna karta, bez batchu = jeden objekt / jeden commit. */
export function workSummaryGroupKey(it: DiaryWorkSummaryEntry): string {
  if (it.batchId) return `batch:${it.batchId}`;
  return `single:${it.objectId}|${it.timestamp}`;
}

/** Mapa u všech skupin s platnými souřadnicemi (bod nebo trasa). */
export function shouldShowWorkGroupMap(group: {
  items?: DiaryWorkSummaryEntry[];
}): boolean {
  return collectGroupPoints(group.items ?? []).length > 0;
}

/** Segmenty pro náhled mapy / PDF — celá trasa skupiny. */
export function workGroupRouteSegments(
  items: DiaryWorkSummaryEntry[],
): Array<{ points: RoutePoint[] }> {
  const polyline = buildWorkGroupRoutePolyline(items);
  if (!polyline.length) return [];
  return [{ points: polyline }];
}
